import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Plus, Pencil, Copy, Archive, Trash2, Send, ArrowLeft, EyeOff, CheckCircle2, FileText, RotateCcw
} from 'lucide-react';

const CATEGORIES = ['marketing', 'design', 'tecnologia', 'comercial', 'atendimento', 'comunicacao', 'administrativo', 'outros'];
const EMPLOYMENT = ['full_time', 'part_time', 'contract', 'internship', 'freelance'];

const emptyDraft = {
  title_pt: '', description_pt: '', requirements_pt: '',
  location: 'Praia, Cabo Verde', category: 'marketing',
  employment_type: 'full_time', vacancies_count: 1,
  salary_range_min: '', salary_range_max: '', currency: 'CVE',
};

function StatusBadge({ s, t }) {
  if (s === 'active') return <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5 text-[11px] font-semibold"><CheckCircle2 className="h-3 w-3"/> {t('adminDrafts.statusActive')}</span>;
  if (s === 'archived') return <span className="inline-flex items-center gap-1 text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 text-[11px] font-semibold"><Archive className="h-3 w-3"/> {t('adminDrafts.statusArchived')}</span>;
  return <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5 text-[11px] font-semibold"><EyeOff className="h-3 w-3"/> {t('adminDrafts.statusDraft')}</span>;
}

export default function AdminDrafts() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ draft: 0, active: 0, archived: 0 });
  const [filter, setFilter] = useState(''); // '' | 'admin_draft' | 'active' | 'archived'
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // job object being edited, or null
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const r = await apiClient.get(`/admin/jobs/drafts`, { params });
      setItems(r.data.items || []);
      setCounts(r.data.counts || { draft: 0, active: 0, archived: 0 });
    } catch (e) {
      toast.error(t('adminDrafts.errorLoad'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openCreate = () => { setEditing(null); setForm(emptyDraft); setFormOpen(true); };
  const openEdit = (job) => {
    setEditing(job);
    setForm({
      ...emptyDraft, ...job,
      salary_range_min: job.salary_range_min ?? '',
      salary_range_max: job.salary_range_max ?? '',
    });
    setFormOpen(true);
  };

  const save = async () => {
    if (!form.title_pt.trim()) { toast.error(t('adminDrafts.errorNoTitle')); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        salary_range_min: form.salary_range_min ? parseInt(form.salary_range_min, 10) : null,
        salary_range_max: form.salary_range_max ? parseInt(form.salary_range_max, 10) : null,
        vacancies_count: parseInt(form.vacancies_count || 1, 10),
      };
      if (editing) {
        await apiClient.patch(`/admin/jobs/drafts/${editing.id}`, payload);
        toast.success(t('adminDrafts.updated'));
      } else {
        await apiClient.post(`/admin/jobs/drafts`, payload);
        toast.success(t('adminDrafts.created'));
      }
      setFormOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('adminDrafts.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  const action = async (id, endpoint, successMsg, opts = {}) => {
    try {
      await apiClient.request({
        method: opts.method || 'post',
        url: `/admin/jobs/drafts/${id}${endpoint}`,
      });
      toast.success(successMsg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('adminDrafts.errorOperation'));
    }
  };

  const publish = (id) => {
    if (!window.confirm(t('adminDrafts.confirmPublish'))) return;
    action(id, '/publish', t('adminDrafts.published'));
  };
  const unpublish = (id) => {
    if (!window.confirm(t('adminDrafts.confirmUnpublish'))) return;
    action(id, '/unpublish', t('adminDrafts.unpublished'));
  };
  const duplicate = (id) => action(id, '/duplicate', t('adminDrafts.duplicated'));
  const archive = (id) => {
    if (!window.confirm(t('adminDrafts.confirmArchive'))) return;
    action(id, '/archive', t('adminDrafts.archived'));
  };
  const restore = (id) => action(id, '/restore', t('adminDrafts.restored'));
  const remove = (id) => {
    if (!window.confirm(t('adminDrafts.confirmDelete'))) return;
    action(id, '', t('adminDrafts.deleted'), { method: 'delete' });
  };

  const filterTabs = [
    { v: '', i18nKey: 'all', count: counts.draft + counts.active + counts.archived },
    { v: 'admin_draft', i18nKey: 'drafts', count: counts.draft },
    { v: 'active', i18nKey: 'published', count: counts.active },
    { v: 'archived', i18nKey: 'archivedTab', count: counts.archived },
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="admin-drafts-page">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{t('adminDrafts.title')}</h1>
            <p className="text-slate-600 text-sm mt-1 max-w-xl">
              {t('adminDrafts.subtitle')}
            </p>
          </div>
          <Button onClick={openCreate} className="bg-slate-900 hover:bg-black" data-testid="btn-new-draft">
            <Plus className="h-4 w-4 mr-2"/> {t('adminDrafts.newInternalJob')}
          </Button>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {filterTabs.map((tb) => (
            <button
              key={tb.v}
              onClick={() => setFilter(tb.v)}
              data-testid={`tab-${tb.v || 'all'}`}
              className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                filter === tb.v
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-slate-900'
              }`}
            >
              {t(`adminDrafts.tab.${tb.i18nKey}`)} <span className="opacity-70">({tb.count})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-center text-slate-500 py-16">{t('adminDrafts.loading')}</p>
        ) : items.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-slate-300 mb-3"/>
            <p className="font-semibold text-slate-900">{t('adminDrafts.emptyTitle')}</p>
            <p className="text-sm text-slate-500 mt-1">{t('adminDrafts.emptyBody')}</p>
            <Button onClick={openCreate} className="mt-6" data-testid="btn-empty-create">
              <Plus className="h-4 w-4 mr-2"/> {t('adminDrafts.createFirst')}
            </Button>
          </div>
        ) : (
          <div className="grid gap-3">
            {items.map((j) => (
              <div key={j.id} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-sm transition-shadow" data-testid={`draft-row-${j.id}`}>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-bold text-slate-900 truncate">{j.title_pt}</h3>
                      <StatusBadge s={j.status} t={t} />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {j.location} · {j.employment_type} · {j.category}
                      {j.salary_range_min && ` · ${j.salary_range_min?.toLocaleString()} - ${j.salary_range_max?.toLocaleString()} ${j.currency}`}
                      {j.vacancies_count > 1 && ` · ${t('adminDrafts.vacancies', { count: j.vacancies_count })}`}
                    </p>
                    {j.published_at && (
                      <p className="text-[11px] text-emerald-700 mt-1">
                        {t('adminDrafts.publishedOn', { date: new Date(j.published_at).toLocaleDateString('pt-PT') })}
                        {j.expires_at && ` · ${t('adminDrafts.expiresOn', { date: new Date(j.expires_at).toLocaleDateString('pt-PT') })}`}
                        {typeof j.applications_count === 'number' && ` · ${t('adminDrafts.applicationsCount', { count: j.applications_count })}`}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(j)} data-testid={`btn-edit-${j.id}`}>
                      <Pencil className="h-3.5 w-3.5 mr-1"/> {t('adminDrafts.edit')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => duplicate(j.id)} data-testid={`btn-dup-${j.id}`}>
                      <Copy className="h-3.5 w-3.5 mr-1"/> {t('adminDrafts.duplicate')}
                    </Button>
                    {j.status === 'admin_draft' && (
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => publish(j.id)} data-testid={`btn-publish-${j.id}`}>
                        <Send className="h-3.5 w-3.5 mr-1"/> {t('adminDrafts.publish')}
                      </Button>
                    )}
                    {j.status === 'active' && (
                      <Button size="sm" variant="outline" onClick={() => unpublish(j.id)} data-testid={`btn-unpublish-${j.id}`}>
                        <EyeOff className="h-3.5 w-3.5 mr-1"/> {t('adminDrafts.unpublish')}
                      </Button>
                    )}
                    {j.status !== 'archived' ? (
                      <Button size="sm" variant="outline" onClick={() => archive(j.id)} data-testid={`btn-archive-${j.id}`}>
                        <Archive className="h-3.5 w-3.5 mr-1"/> {t('adminDrafts.archive')}
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => restore(j.id)} data-testid={`btn-restore-${j.id}`}>
                        <RotateCcw className="h-3.5 w-3.5 mr-1"/> {t('adminDrafts.restore')}
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => remove(j.id)} data-testid={`btn-delete-${j.id}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1"/> {t('adminDrafts.delete')}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Editor dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="draft-editor">
          <DialogHeader>
            <DialogTitle>{editing ? t('adminDrafts.editDraft') : t('adminDrafts.newInternalJob')}</DialogTitle>
            <p className="text-sm text-slate-500">
              {t('adminDrafts.editorHint')}
            </p>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">{t('adminDrafts.fieldTitle')} *</label>
              <Input value={form.title_pt} onChange={(e) => setForm({...form, title_pt: e.target.value})} placeholder="Ex.: Gestor de Marketing Digital" data-testid="input-title"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">{t('adminDrafts.fieldCategory')}</label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger data-testid="input-category"><SelectValue/></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{t(`adminDrafts.category.${c}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">{t('adminDrafts.fieldType')}</label>
                <Select value={form.employment_type} onValueChange={(v) => setForm({...form, employment_type: v})}>
                  <SelectTrigger data-testid="input-employment"><SelectValue/></SelectTrigger>
                  <SelectContent>{EMPLOYMENT.map(c => <SelectItem key={c} value={c}>{t(`adminDrafts.employment.${c}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">{t('adminDrafts.fieldLocation')}</label>
                <Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} data-testid="input-location"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">{t('adminDrafts.fieldVacancies')}</label>
                <Input type="number" min={1} value={form.vacancies_count} onChange={(e) => setForm({...form, vacancies_count: e.target.value})} data-testid="input-vacancies"/>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">{t('adminDrafts.fieldSalaryMin')}</label>
                <Input type="number" value={form.salary_range_min} onChange={(e) => setForm({...form, salary_range_min: e.target.value})} placeholder={t('adminDrafts.optional')}/>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">{t('adminDrafts.fieldSalaryMax')}</label>
                <Input type="number" value={form.salary_range_max} onChange={(e) => setForm({...form, salary_range_max: e.target.value})} placeholder={t('adminDrafts.optional')}/>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">{t('adminDrafts.fieldCurrency')}</label>
                <Select value={form.currency} onValueChange={(v) => setForm({...form, currency: v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CVE">CVE</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">{t('adminDrafts.fieldDescription')}</label>
              <Textarea value={form.description_pt} onChange={(e) => setForm({...form, description_pt: e.target.value})} rows={5} placeholder="O que a pessoa vai fazer no dia-a-dia…" data-testid="input-description"/>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1 block">{t('adminDrafts.fieldRequirements')}</label>
              <Textarea value={form.requirements_pt} onChange={(e) => setForm({...form, requirements_pt: e.target.value})} rows={4} placeholder="Formação, experiência, idiomas, ferramentas…" data-testid="input-requirements"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} data-testid="btn-cancel-editor">{t('adminDrafts.cancel')}</Button>
            <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-black" data-testid="btn-save-draft">
              {saving ? t('adminDrafts.saving') : editing ? t('adminDrafts.saveChanges') : t('adminDrafts.createDraft')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
